import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { config } from '@/config';
import * as Sentry from '@sentry/react';
import { isFirebaseOfflineError } from '@/services/errorReportingService';

export interface ComparisonFeature {
  label: string;
  free: boolean | string;
  pro: boolean | string;
  icon?: string;
}

export interface OnboardingComparisonData {
  freeTitle: string;
  freeDescription: string;
  proTitle: string;
  proDescription: string;
  trialDays?: number;
  features: ComparisonFeature[];
  lastUpdated?: string;
}

const ONBOARDING_COLLECTION = 'onboarding_comparison';
const ONBOARDING_DOC_ID = 'trial_welcome';

/**
 * Fetch onboarding comparison data from API
 */
export const fetchOnboardingComparisonFromAPI = async (): Promise<OnboardingComparisonData> => {
  try {
    const response = await fetch(`${config.apiUrl}/api/onboarding/comparison`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch comparison data');
    }

    const data = await response.json();
    // Data fetched successfully from API
    return data;
  } catch (error) {
    Sentry.captureException(error, {
      tags: { feature: 'onboarding-comparison' },
      extra: { context: 'fetching from API, falling back to defaults' },
    });
    return getDefaultComparisonData();
  }
};

/**
 * Fetch onboarding comparison data from Firebase (direct)
 */
export const fetchOnboardingComparisonFromFirebase = async (): Promise<OnboardingComparisonData> => {
  try {
    // Fetching from Firebase

    // First, let's see what documents exist in this collection
    const collectionRef = collection(db, ONBOARDING_COLLECTION);
    const querySnapshot = await getDocs(collectionRef);
    // Check available documents in collection

    // Now try to get the specific document
    const docRef = doc(db, ONBOARDING_COLLECTION, ONBOARDING_DOC_ID);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data() as OnboardingComparisonData;
      // Data fetched successfully from Firebase
      return data;
    } else {
      // If the specific document doesn't exist, try to get the first document in the collection
      if (!querySnapshot.empty) {
        const firstDoc = querySnapshot.docs[0];
        const data = firstDoc.data() as OnboardingComparisonData;
        // Using first available document from collection
        return data;
      }

      // No document found, using default data
      return getDefaultComparisonData();
    }
  } catch (error) {
    if (!isFirebaseOfflineError(error)) {
      Sentry.captureException(error, {
        tags: { feature: 'onboarding-comparison' },
        extra: { context: 'fetching onboarding comparison data from Firebase' },
      });
    }
    return getDefaultComparisonData();
  }
};

/**
 * Main fetch function - tries API first, then Firebase, then defaults
 */
export const fetchOnboardingComparison = async (): Promise<OnboardingComparisonData> => {
  // Try API first (if you implement it)
  // return fetchOnboardingComparisonFromAPI();

  // For now, use Firebase directly
  return fetchOnboardingComparisonFromFirebase();
};

/**
 * Default comparison data as fallback
 */
export const getDefaultComparisonData = (): OnboardingComparisonData => ({
  freeTitle: 'Free Plan',
  freeDescription: 'What you had before',
  proTitle: 'Pro Plan',
  proDescription: 'Your trial includes all these features',
  trialDays: 14,
  features: [
    {
      label: 'Monthly Videos',
      free: '5 videos',
      pro: '60 videos',
      icon: 'VideoSettings',
    },
    {
      label: 'Video Duration',
      free: '5 minutes',
      pro: '10 minutes',
      icon: 'AccessTime',
    },
    {
      label: 'Video Quality',
      free: '720p',
      pro: '1080p HD',
      icon: 'Star',
    },
    {
      label: 'Storage Duration',
      free: '30 days',
      pro: 'Unlimited',
      icon: 'CloudUpload',
    },
    {
      label: 'AI Title',
      free: true,
      pro: true,
      icon: 'AutoAwesome',
    },
    {
      label: 'AI Summary',
      free: true,
      pro: true,
      icon: 'AutoAwesome',
    },
    {
      label: 'AI Chapters',
      free: true,
      pro: true,
      icon: 'AutoAwesome',
    },
    {
      label: 'AI Content Analysis',
      free: false,
      pro: true,
      icon: 'AutoAwesome',
    },
    {
      label: 'AI Key Points',
      free: false,
      pro: true,
      icon: 'AutoAwesome',
    },
    {
      label: 'AI Tagging',
      free: false,
      pro: true,
      icon: 'AutoAwesome',
    },
    {
      label: 'Tag Management',
      free: false,
      pro: true,
      icon: 'Label',
    },
    {
      label: 'Commenting',
      free: false,
      pro: true,
      icon: 'Group',
    },
  ],
});
